import { useState } from "react";
import { Ticket, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { api, apiError } from "../../lib/api";
import { formatARS } from "../../lib/site";
import { TicketQR } from "./TicketQR";

const STATUS = (b) => {
  if (b.status === "reservado") return `Sin pagar · vence ${new Date(b.expires_at).toLocaleString("es-AR", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/Argentina/Buenos_Aires" })}`;
  if (b.status === "vencida") return "Reserva vencida: las butacas se liberaron";
  return "Pagado";
};

const TicketView = ({ b }) => (
  <div data-testid="my-ticket-result" className="ticket-cut mt-4 rounded-2xl bg-[var(--forest)] p-6 text-white">
    <p className="font-mono text-xs tracking-[0.25em] text-[var(--lime)]">{b.code}</p>
    <p data-testid="my-ticket-status" className="mt-2 inline-block rounded-full bg-white/15 px-3 py-1 text-xs font-bold">{STATUS(b)}</p>
    <p className="mt-3 font-display text-2xl">{b.origin} → {b.destination}</p>
    <div className="mt-4 grid grid-cols-3 gap-3 font-mono text-xs text-white/85">
      <span>{b.date}</span><span>{b.departure} hs</span><span>{b.service}</span>
    </div>
    <div className="mt-4 flex justify-between border-t border-dashed border-white/30 pt-4 text-sm">
      <span>{b.name} · {b.passengers} pasajero(s)</span><span className="font-semibold">{formatARS(b.total)}</span>
    </div>
    <div className="mt-4 flex items-center gap-4 border-t border-dashed border-white/30 pt-4">
      <TicketQR code={b.code} size={110} />
      <p className="text-sm text-white/85">Butacas {(b.seats ?? []).join(", ")}. Mostrá este QR al conductor al subir.{b.ticket_url && <a href={b.ticket_url} target="_blank" rel="noreferrer" data-testid="my-ticket-save-phone" className="mt-2 block font-bold text-[var(--lime)] underline">Guardar en el celular</a>}</p>
    </div>
  </div>
);

export const MyTicketsDialog = ({ dark = false }) => {
  const [code, setCode] = useState("");
  const [dni, setDni] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/bookings/${encodeURIComponent(code)}`, { params: { dni } });
      setResult(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button data-testid="my-tickets-button" className={`inline-flex items-center gap-2 rounded-full border-2 px-5 py-2.5 text-sm font-bold transition-[background-color,color] duration-300 ${dark ? "w-full justify-center border-[var(--lime)] text-[var(--lime)] hover:bg-[var(--lime)] hover:text-[var(--ink)]" : "border-[var(--forest)] text-[var(--forest)] hover:bg-[var(--forest)] hover:text-white"}`}>
          <Ticket className="h-4 w-4" /> Mis pasajes
        </button>
      </DialogTrigger>
      <DialogContent data-testid="my-tickets-dialog" className="max-w-md rounded-3xl" data-lenis-prevent>
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Mis pasajes</DialogTitle>
          <DialogDescription>Ingresá el código de reserva y tu DNI.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="my-tickets-code-input" placeholder="Código (ej. SG-AB12CD)" value={code} onChange={(e) => setCode(e.target.value)} required className="h-12 rounded-xl font-mono uppercase" />
          <Input data-testid="my-tickets-dni-input" placeholder="DNI" inputMode="numeric" value={dni} onChange={(e) => setDni(e.target.value)} required className="h-12 rounded-xl" />
          <button data-testid="my-tickets-submit" disabled={loading} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[var(--forest)] font-bold text-white transition-colors hover:bg-[var(--forest-2)]">
            {loading && <Loader2 className="h-4 w-4 animate-spin" />} Buscar mi pasaje
          </button>
        </form>
        {error && <p data-testid="my-tickets-error" className="text-sm font-medium text-red-700">{error}</p>}
        {result && <TicketView b={result} />}
      </DialogContent>
    </Dialog>
  );
};
